import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  TrendingUp, 
  DollarSign, 
  Target,
  Clock,
  Download,
  PieChart,
  AlertTriangle,
  RefreshCw
} from 'lucide-react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,

  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts'
import { ManualTestTrade, ManualTestStats } from '../types'
import {
  getManualTestTrades,
  getManualTestStats
} from '../lib/api'
import { supabase } from '../lib/supabase'

interface ManualTestStatsPageProps {
  strategyId?: string
}

export default function ManualTestStatsPage({ strategyId: propStrategyId }: ManualTestStatsPageProps) {
  const { strategyId: paramStrategyId } = useParams<{ strategyId: string }>()
  const strategyId = propStrategyId || paramStrategyId
  const navigate = useNavigate()


  const [trades, setTrades] = useState<ManualTestTrade[]>([])
  const [stats, setStats] = useState<ManualTestStats | null>(null)
  const [strategyName, setStrategyName] = useState('')
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  })
  const [selectedPeriod, setSelectedPeriod] = useState<'all' | 'week' | 'month' | 'quarter'>('all')

  // 获取策略信息
  const fetchStrategyInfo = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('No session found')
      }

      const { data, error } = await supabase
        .from('strategies')
        .select('name, market_symbol, timeframe, type')
        .eq('id', strategyId)
        .single()

      if (error) throw error
      setStrategyName(data.name)
    } catch (error) {
      console.error('获取策略信息失败:', error)
    }
  }

  // 获取交易记录
  const fetchTrades = async () => {
    try {
      const data = await getManualTestTrades(strategyId!)
      
      // 客户端筛选
      let filteredData = data || []
      
      // 应用日期筛选
      if (dateRange.start) {
        filteredData = filteredData.filter((trade: any) => trade.entry_time >= dateRange.start)
      }
      if (dateRange.end) {
        filteredData = filteredData.filter((trade: any) => trade.entry_time <= dateRange.end)
      }

      // 按时间排序
      filteredData.sort((a: any, b: any) => new Date(a.entry_time).getTime() - new Date(b.entry_time).getTime())
      
      setTrades(filteredData)
    } catch (error) {
      console.error('获取交易记录失败:', error)
      // 如果获取交易记录失败，设置空数组
      setTrades([])
      console.debug('Trades loading failed, using empty array')
    }
  }

  // 获取统计数据
  const fetchStats = async () => {
    try {
      const data = await getManualTestStats(strategyId!)
      setStats(data)
    } catch (error) {
      console.error('获取统计数据失败:', error)
      // 如果获取统计数据失败，设置默认值
      setStats({
        total_trades: 0,
        winning_trades: 0,
        losing_trades: 0,
        win_rate: 0,
        avg_r_value: 0,
        max_r_value: 0,
        min_r_value: 0,
        max_drawdown: 0,
        total_pnl: 0,
        avg_pnl_per_trade: 0,
        long_trades: 0,
        short_trades: 0,
        long_win_rate: 0,
        short_win_rate: 0,
        avg_trade_duration: 0,
        manual_override_count: 0,
        manual_override_rate: 0
      })
    }
  }

  useEffect(() => {
    if (strategyId) {
      fetchStrategyInfo()
      fetchTrades()
      fetchStats()
    }
  }, [strategyId, dateRange])

  // 计算图表数据
  const chartData = useMemo(() => {
    if (trades.length === 0) return {
      monthlyReturns: [],
      rValueRanges: [],
      directionData: [],
      pnlData: [],
      equityCurve: [],
      drawdownData: []
    }

    // 月度收益数据
    const monthlyReturns = trades.reduce((acc, trade) => {
      const month = new Date(trade.entry_time).toISOString().slice(0, 7)
      if (!acc[month]) {
        acc[month] = { month, pnl: 0, trades: 0, winRate: 0, wins: 0 }
      }
      acc[month].pnl += trade.pnl_amount
      acc[month].trades += 1
      if (trade.pnl_amount > 0) {
        acc[month].wins += 1
      }
      return acc
    }, {} as Record<string, any>)

    Object.values(monthlyReturns).forEach((month: any) => {
      month.winRate = month.trades > 0 ? (month.wins / month.trades) * 100 : 0
    })

    // R值分布数据
    const rValueRanges = [
      { range: '负R值', min: -Infinity, max: 0, count: 0, color: '#EF4444' },
      { range: '0-1R', min: 0, max: 1, count: 0, color: '#F59E0B' },
      { range: '1-2R', min: 1, max: 2, count: 0, color: '#10B981' },
      { range: '2-3R', min: 2, max: 3, count: 0, color: '#3B82F6' },
      { range: '3R+', min: 3, max: Infinity, count: 0, color: '#8B5CF6' }
    ]

    trades.forEach(trade => {
      const rValue = trade.pnl_r
      for (const range of rValueRanges) {
        if (rValue >= range.min && rValue < range.max) {
          range.count++
          break
        }
      }
    })

    // 方向分布数据
    const directionData = [
      { name: '多头', value: trades.filter(t => t.direction === 'long').length, color: '#10B981' },
      { name: '空头', value: trades.filter(t => t.direction === 'short').length, color: '#EF4444' }
    ]

    // 盈亏分布数据
    const pnlData = [
      { name: '盈利', value: trades.filter(t => t.pnl_amount > 0).length, color: '#10B981' },
      { name: '亏损', value: trades.filter(t => t.pnl_amount <= 0).length, color: '#EF4444' }
    ]

    // 资金曲线数据
    let cumulativePnL = 0
    const equityCurve = trades.map((trade, index) => {
      cumulativePnL += trade.pnl_amount
      return {
        tradeNumber: index + 1,
        cumulativePnL,
        date: new Date(trade.entry_time).toLocaleDateString(),
        pnl: trade.pnl_amount,
        rValue: trade.pnl_r
      }
    })

    // 回撤数据
    let peak = 0
    const drawdownData = equityCurve.map(point => {
      if (point.cumulativePnL > peak) {
        peak = point.cumulativePnL
      }
      const drawdown = peak - point.cumulativePnL
      return {
        ...point,
        drawdown,
        drawdownPercent: peak > 0 ? (drawdown / peak) * 100 : 0
      }
    })

    return {
      monthlyReturns: Object.values(monthlyReturns),
      rValueRanges: rValueRanges.filter(r => r.count > 0),
      directionData,
      pnlData,
      equityCurve,
      drawdownData
    }
  }, [trades])

  // 快速筛选按钮
  const quickFilters = [
    { label: '全部', value: 'all' },
    { label: '本周', value: 'week' },
    { label: '本月', value: 'month' },
    { label: '本季度', value: 'quarter' }
  ]

  const handleQuickFilter = (period: string) => {
    const now = new Date()
    let startDate = ''

    switch (period) {
      case 'week':
        const weekStart = new Date(now)
        weekStart.setDate(now.getDate() - now.getDay())
        startDate = weekStart.toISOString().slice(0, 10)
        break
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
        break
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3)
        startDate = new Date(now.getFullYear(), quarter * 3, 1).toISOString().slice(0, 10)
        break
      default:
        startDate = ''
    }

    setDateRange({ start: startDate, end: now.toISOString().slice(0, 10) })
    setSelectedPeriod(period as any)
  }

  // 导出报告
  const exportReport = () => {
    if (!stats || trades.length === 0) {
      return
    }

    const reportData = {
      strategyName,
      reportDate: new Date().toLocaleDateString(),
      stats,
      trades: trades.length,
      dateRange: dateRange.start && dateRange.end ? 
        `${dateRange.start} 至 ${dateRange.end}` : '全部时间',
      summary: {
        totalTrades: stats.total_trades,
        winRate: (stats.win_rate * 100).toFixed(1) + '%',
        totalPnL: stats.total_pnl.toFixed(2),
        avgRValue: stats.avg_r_value.toFixed(2),
        maxDrawdown: stats.max_drawdown.toFixed(2),
        avgTradeDuration: stats.avg_trade_duration.toFixed(1) + '小时'
      }
    }

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `manual_test_report_${strategyName}_${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-md hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {strategyName} - 手动测试统计分析
                </h1>
                <p className="text-sm text-gray-500">
                  详细统计报告和图表分析
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => { fetchTrades(); fetchStats(); }}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                刷新数据
              </button>
              <button
                onClick={exportReport}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                导出报告
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 快速筛选 */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">快速筛选</h3>
            
            {/* 快速筛选按钮 - 小屏幕下简化 */}
            <div className="flex flex-wrap gap-2 mb-4">
              {quickFilters.map(filter => (
                <button
                  key={filter.value}
                  onClick={() => handleQuickFilter(filter.value)}
                  className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md ${
                    selectedPeriod === filter.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            
            {/* 日期选择器 - 小屏幕下垂直布局 */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">开始日期</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">结束日期</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 核心统计指标 */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Target className="w-5 h-5 text-blue-600" />
                <span className="ml-2 text-sm text-gray-600">总交易</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.total_trades}</p>
              <p className="text-xs text-gray-500">多头: {stats.long_trades} | 空头: {stats.short_trades}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <span className="ml-2 text-sm text-gray-600">胜率</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{(stats.win_rate * 100).toFixed(1)}%</p>
              <p className="text-xs text-gray-500">盈利: {stats.winning_trades} | 亏损: {stats.losing_trades}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <DollarSign className="w-5 h-5 text-purple-600" />
                <span className="ml-2 text-sm text-gray-600">总盈亏</span>
              </div>
              <p className={`text-2xl font-bold ${stats.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.total_pnl >= 0 ? '+' : ''}{stats.total_pnl.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">平均: {stats.avg_pnl_per_trade.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Target className="w-5 h-5 text-indigo-600" />
                <span className="ml-2 text-sm text-gray-600">平均R值</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.avg_r_value.toFixed(2)}</p>
              <p className="text-xs text-gray-500">最大: {stats.max_r_value.toFixed(2)} | 最小: {stats.min_r_value.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-orange-600" />
                <span className="ml-2 text-sm text-gray-600">平均持仓</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.avg_trade_duration.toFixed(1)}h</p>
              <p className="text-xs text-gray-500">总时长: {(stats.avg_trade_duration * stats.total_trades).toFixed(1)}h</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="ml-2 text-sm text-gray-600">干预率</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{(stats.manual_override_rate * 100).toFixed(1)}%</p>
              <p className="text-xs text-gray-500">干预: {stats.manual_override_count} | 正常: {stats.total_trades - stats.manual_override_count}</p>
            </div>
          </div>
        )}

        {/* 图表区域 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* 资金曲线图 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">资金曲线</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData.equityCurve}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tradeNumber" />
                <YAxis />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="cumulativePnL" 
                  stroke="#3B82F6" 
                  fill="#3B82F6" 
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* 回撤图 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">回撤分析</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.drawdownData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tradeNumber" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="drawdownPercent" 
                  stroke="#EF4444" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* R值分布图 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">R值分布</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.rValueRanges}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ range, count }) => `${range}: ${count}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {chartData.rValueRanges.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 交易方向分布 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">交易方向分布</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.directionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.directionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 月度收益分析 */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">月度收益分析</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData.monthlyReturns}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="pnl" fill="#3B82F6" name="月度盈亏" />
              <Line yAxisId="right" type="monotone" dataKey="winRate" stroke="#EF4444" name="胜率%" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 详细统计表格 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">详细统计</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    统计项目
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    数值
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    说明
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">总交易笔数</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.total_trades || 0}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">测试期间的总交易数量</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">盈利交易</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.winning_trades || 0}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">PnL &gt; 0 的交易数量</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">亏损交易</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.losing_trades || 0}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">PnL ≤ 0 的交易数量</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">胜率</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{(stats?.win_rate || 0) * 100}%</td>
                  <td className="px-6 py-4 text-sm text-gray-500">盈利交易占总交易的比例</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">总盈亏</td>
                  <td className={`px-6 py-4 text-sm font-medium ${(stats?.total_pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(stats?.total_pnl || 0) >= 0 ? '+' : ''}{stats?.total_pnl.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">所有交易的累计盈亏</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">平均每笔盈亏</td>
                  <td className={`px-6 py-4 text-sm font-medium ${(stats?.avg_pnl_per_trade || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(stats?.avg_pnl_per_trade || 0) >= 0 ? '+' : ''}{stats?.avg_pnl_per_trade.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">总盈亏除以交易笔数</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">平均R值</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.avg_r_value.toFixed(2) || '0.00'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">所有交易R值的平均值</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">最大R值</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.max_r_value.toFixed(2) || '0.00'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">单笔交易的最大R值</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">最小R值</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.min_r_value.toFixed(2) || '0.00'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">单笔交易的最小R值</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">最大回撤</td>
                  <td className="px-6 py-4 text-sm text-red-600">{stats?.max_drawdown.toFixed(2) || '0.00'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">资金曲线从峰值到谷值的最大跌幅</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">平均持仓时间</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.avg_trade_duration.toFixed(1) || '0.0'}小时</td>
                  <td className="px-6 py-4 text-sm text-gray-500">从入场到出场的平均时间</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">多头交易</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.long_trades || 0}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">做多方向的交易数量</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">空头交易</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.short_trades || 0}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">做空方向的交易数量</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">多头胜率</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{(stats?.long_win_rate || 0) * 100}%</td>
                  <td className="px-6 py-4 text-sm text-gray-500">多头交易的盈利比例</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">空头胜率</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{(stats?.short_win_rate || 0) * 100}%</td>
                  <td className="px-6 py-4 text-sm text-gray-500">空头交易的盈利比例</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">主观干预</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{stats?.manual_override_count || 0}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">标记为手动干预的交易数量</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">干预率</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{(stats?.manual_override_rate || 0) * 100}%</td>
                  <td className="px-6 py-4 text-sm text-gray-500">主观干预交易占总交易的比例</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
} 